import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useMomentos } from "@/context/MomentosContext";
import api from "@/lib/api";

// Tour de globos, uno por vez. Resalta el elemento (spotlight) y muestra un cartelito.
// Se muestra una sola vez por usuario (persistido en usuario.tours_vistos vía POST /api/tour-visto).
// COLA: nunca se abre mientras hay un Momento de conciencia en pantalla; espera a que se cierre.
export default function Tour({ id, steps, enabled = true }) {
  const { user, setUser } = useAuth();
  const { hayMomento } = useMomentos();
  const yaVisto = !!user?.tours_vistos?.includes(id);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [activo, setActivo] = useState(false);

  useEffect(() => {
    if (enabled && user && !yaVisto && !hayMomento) {
      const t = setTimeout(() => setActivo(true), 500);
      return () => clearTimeout(t);
    }
  }, [enabled, user, yaVisto, hayMomento]);

  const medir = useCallback(() => {
    const step = steps[i];
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom });
  }, [i, steps]);

  useLayoutEffect(() => {
    if (!activo) return;
    medir();
    const t = setTimeout(medir, 260);
    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", medir);
      window.removeEventListener("scroll", medir, true);
    };
  }, [activo, medir]);

  const cerrar = async () => {
    setActivo(false);
    try {
      await api.post("/tour-visto", { tour: id });
      setUser((u) => (u ? { ...u, tours_vistos: [...(u.tours_vistos || []), id] } : u));
    } catch {}
  };

  const siguiente = () => {
    if (i >= steps.length - 1) cerrar();
    else setI((n) => n + 1);
  };

  if (!activo || !user || yaVisto || hayMomento) return null;

  const pad = 8;
  const box = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  // Posición del cartelito: debajo del elemento; si no entra, arriba.
  const cardW = 340;
  const cardH = 190;
  let cardTop = box ? box.top + box.height + 14 : window.innerHeight / 2 - cardH / 2;
  const abajoNoEntra = box && box.top + box.height + 14 + cardH > window.innerHeight;
  if (abajoNoEntra) cardTop = Math.max(16, box.top - cardH - 14);
  let cardLeft = box ? box.left : window.innerWidth / 2 - cardW / 2;
  cardLeft = Math.min(Math.max(16, cardLeft), window.innerWidth - cardW - 16);

  const step = steps[i];

  return (
    <AnimatePresence>
      <motion.div
        key="tour"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80]"
        style={{ pointerEvents: "auto" }}
        data-testid={`tour-${id}`}
      >
        {/* Spotlight: el box transparente con una sombra enorme oscurece todo menos el elemento */}
        {box ? (
          <div
            className="fixed rounded-xl"
            style={{
              top: box.top, left: box.left, width: box.width, height: box.height,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.74)",
              border: "2px solid #C9A84C",
              transition: "all 0.25s ease",
            }}
          />
        ) : (
          <div className="fixed inset-0" style={{ background: "rgba(0,0,0,0.74)" }} />
        )}

        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}
          className="fixed card-dark p-5"
          style={{ top: cardTop, left: cardLeft, width: cardW, borderColor: "rgba(201,168,76,0.5)", background: "#141414" }}
          data-testid={`tour-${id}-globo`}
        >
          <p className="text-crema text-sm leading-relaxed mb-5" data-testid={`tour-${id}-texto`}>{step.texto}</p>
          <div className="flex items-center justify-between">
            <button onClick={cerrar} className="mono-label text-gris hover:text-crema transition-colors" data-testid={`tour-${id}-saltear`}>
              SALTEAR
            </button>
            <div className="flex items-center gap-3">
              <span className="mono-label text-gris" data-testid={`tour-${id}-paso`}>{i + 1}/{steps.length}</span>
              <button onClick={siguiente} className="btn-dorado" data-testid={`tour-${id}-siguiente`}>
                {i >= steps.length - 1 ? "LISTO" : "SIGUIENTE"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
